import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/ClientPortal.js
//     MAX_FILE_SIZE_MB = 10 (per file)
//     ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.heic,.webp'
//     User-facing label: "PDF, JPG, PNG, HEIC, WEBP · up to 10MB each"
//     Bulk upload zone at top of upload page with drag-and-drop, Choose
//       files, and Take photo (mobile) buttons
//     StagedFiles strip lets client attach uploaded files to transactions
//       via drag-to-row or tap-file-then-tap-row
//     Per-row upload button also available as alternative
//     capture="environment" attribute triggers rear camera on mobile
// Removed:
//   - "Anything else (HEIF, WebP, raw image formats) is rejected" -- WebP
//     IS accepted. (HEIC and HEIF often referred to interchangeably; the
//     .heic extension is accepted.)
//   - "Upload on client's behalf" feature -- does not exist on the
//     bookkeeper side. Bookkeepers can't upload files to a transaction
//     directly from the request page.
//   - "Tap upload button, three options will appear" -- not accurate.
//     Real flow: bulk-upload zone at top OR per-row upload button.
//     "Take photo" is a separate button on mobile.
// Pass 2 added:
//   - The bulk upload zone is the primary upload mechanism (old article
//     described only per-row uploads)
//   - GIF is also accepted (not in old article)
//   - Drag-to-row / tap-to-attach flow for staged files

export default {
  title:   "A client can't upload a file",
  summary: 'File size and format limits, browser issues, mobile-specific tips, and how to help your client over the phone.',
  body: (
    <>
      <P>
        Most upload failures fall into one of three categories: the
        file is too big, the file is the wrong format, or the
        browser is too old. This guide walks through each, with
        plain English you can read out to a client over the phone.
      </P>

      <Callout variant="info" title="What the client sees first">
        At the top of the upload page is a single "Upload all your
        receipts at once" zone with a drag-and-drop area, a "Choose
        files" button, and on mobile a "Take photo" button. After
        uploading, files appear as chips at the top of the page. The
        client then drags each chip to a transaction (or taps the
        chip then taps the transaction) to attach it. There's also a
        per-row upload button as an alternative for one-at-a-time
        uploads.
      </Callout>

      <H>The file is too big</H>
      <P>
        The upload cap is <strong>10 MB per file</strong>. Photos
        taken on modern phones can easily exceed that, especially on
        iPhones in the highest quality setting. When a file is over
        the limit, Receipt Chaser rejects it before the upload starts
        and shows the file name with the over-limit message.
      </P>
      <P>
        Two ways to get under the limit:
      </P>
      <UL>
        <Li>
          <strong>Take a fresh photo at a lower setting.</strong> On
          iPhone, ask the client to use the standard Camera app rather
          than any specialist scanning app. Take a normal photo rather
          than a Live Photo or Portrait. The result is usually well
          under 5 MB.
        </Li>
        <Li>
          <strong>Email it to themselves first.</strong> Many mail
          apps automatically compress images on send. The version that
          lands in their own inbox is often less than half the size of
          the original. They can then upload that version from their
          email rather than from their camera roll.
        </Li>
      </UL>

      <H>The file is the wrong format</H>
      <P>
        Receipt Chaser accepts: <strong>PDF, JPG/JPEG, PNG, GIF, HEIC
        (iPhone default), and WebP</strong>. Anything else (raw image
        formats, Word documents, spreadsheets, .pages, .numbers) is
        rejected.
      </P>
      <P>
        Common scenarios and the fix:
      </P>
      <UL>
        <Li>
          <strong>"It says my file isn't a valid receipt format."</strong>{' '}
          Ask the client what kind of file it is. If it's a Word doc
          or a spreadsheet, they should export it as a PDF (every
          office program has File &gt; Save As &gt; PDF) and upload
          that.
        </Li>
        <Li>
          <strong>A screenshot from a banking app.</strong>{' '}
          Screenshots are PNG by default, which works fine. Make sure
          they're uploading the file itself, not pasting it into the
          browser.
        </Li>
        <Li>
          <strong>An emailed receipt.</strong> Most emailed receipts
          either are or contain a PDF. Have the client save the PDF
          attachment to their device first, then upload the saved
          file.
        </Li>
      </UL>

      <H>The browser is too old or unusual</H>
      <P>
        The upload page works on every modern browser: Chrome, Safari,
        Firefox, Edge, and the built-in browsers on iPhone and
        Android. It doesn't support Internet Explorer (which Microsoft
        retired in 2022).
      </P>
      <P>
        If a client is using an old browser, they'll typically see a
        message saying the upload button doesn't work, or the page
        won't load at all. The fix is to open the link in a current
        browser instead. The link itself works the same way regardless
        of which browser they open it from.
      </P>

      <H>Mobile-specific issues</H>
      <UL>
        <Li>
          <strong>The Take photo button doesn't appear.</strong> It
          only shows on touch devices (phone, tablet). On desktop the
          "Choose files" button is the only one shown, which is
          correct.
        </Li>
        <Li>
          <strong>Camera opens but nothing happens after taking the
          photo.</strong> Usually a permissions issue. Ask the client
          to check their browser settings allow camera access for
          Receipt Chaser, or to take the photo through their normal
          Camera app first and then use "Choose files" to pick it
          from their photo library.
        </Li>
        <Li>
          <strong>The upload silently hangs.</strong> Usually a flaky
          mobile connection. Ask the client to switch to Wi-Fi and
          try again. If they keep hitting it, they can email the
          photos to themselves and upload from a laptop instead.
        </Li>
        <Li>
          <strong>Files attach to the wrong transaction.</strong> On
          mobile, after uploading a batch the client taps a file chip
          to select it (it gets highlighted) and then taps the
          transaction row to attach. It's easy to skip the highlight
          step and end up tapping a row with no chip selected. The
          chip stays put until they tap a row.
        </Li>
      </UL>

      <H>Helping a client over the phone</H>
      <P>
        Most upload issues are easier to fix once you've walked the
        client through it. A script that works:
      </P>
      <OL>
        <Li>
          "Open the email I sent and tap the upload link. Let me
          know when you see a page listing your transactions."
        </Li>
        <Li>
          "Near the top of the page there's an upload zone. Tap{' '}
          <em>Choose files</em> (or <em>Take photo</em> if you want
          to use the camera). Pick the receipt files."
        </Li>
        <Li>
          "You should now see small chips at the top with your file
          names. Tap one chip to select it, then tap the transaction
          row below to attach it. Repeat for each file."
        </Li>
        <Li>
          "When all files are attached, the chips disappear and the
          transactions go green. You're done."
        </Li>
      </OL>

      <Callout variant="warning" title="If the client genuinely can't manage the upload">
        Receipt Chaser doesn't currently offer an "upload on client's
        behalf" feature on the bookkeeper side. If a client truly
        can't manage the upload themselves (older client, no
        smartphone, hard technical block), the workaround is to have
        them send you the receipts by email or post, then open their
        upload link yourself (copy it from the request page) and
        upload through the same flow. The link is the only thing
        needed to access the upload page; it doesn't require their
        login.
      </Callout>

      <Callout variant="info" title="When it's not the client's problem">
        If multiple clients are reporting upload failures around the
        same time, it might be on our side rather than theirs. Send
        us a message via the <Link to="/contact">Contact</Link> page
        and we'll investigate.
      </Callout>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/client-didnt-get-email">My client says they didn't get the email</Link>
        </Li>
        <Li>
          <Link to="/support/handling-lost-receipts">Handling lost receipts</Link>
        </Li>
      </UL>
    </>
  ),
}
